<template>
    <div>
        <DCard
            padding
        >
            <section class="mb-5">
                <h2 class="h4">
                    {{ __('customers.invoices.customer_section') }}
                </h2>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.id') }}</b>: {{ customer.id }}
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.name') }}</b>: {{ customer.billing_firstname }} {{ customer.billing_lastname }}
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.company') }}</b>: {{ customer.billing_company }}
                    </DCol>
                </DRow>
                <DRow v-if="customer.no_tax_on_invoice === false">
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.tax_rate') }}</b>: {{ taxData.taxRate }}%
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.tax_key') }}</b>: {{ taxData.taxKey }}
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.revenue_account') }}</b>: {{ taxData.revenueAccount }}
                    </DCol>
                </DRow>
                <DRow>
                    <DCol>
                        <b>{{ __('customers.invoices.customer_details.invoice_text') }}</b>: {{ taxData.invoiceText }}
                    </DCol>
                </DRow>
                <DFormRow
                    v-if="tenantSettings.enable_divergent_payment_methods === true"
                    class="w-50"
                    :label="__('customers.customers.payment_method')"
                    :is-required="true"
                    is-vertical
                    :error="errors.payment_method || paymentMethodError"
                >
                    <DSelect
                        id="payment_method"
                        v-model="paymentMethod"
                        :options="availablePaymentMethods"
                        :placeholder="__('customers.customers.select_payment_method')"
                        :is-valid="isFormFieldValid('payment_method')"
                    />
                </DFormRow>
                <DFormRow
                    v-if="tenantSettings.enable_divergent_payment_methods === true"
                    class="w-50"
                    :label="__('customers.customers.payment_term')"
                    is-vertical
                    :error="errors.payment_term"
                >
                    <DSelect
                        id="payment_term"
                        v-model="paymentTerm"
                        :options="paymentTerms"
                        :placeholder="__('customers.customers.select_payment_term')"
                        :is-valid="isFormFieldValid('payment_term')"
                    />
                </DFormRow>
                <DFormRow
                    class="w-50"
                    :label="__('products.products.form.also_display_retired_products')"
                    is-vertical
                    :error="errors.retired"
                >
                    <div style="display: flex; align-items: center; flex-shrink: 0; margin-left: 10px">
                        <DSwitch
                            v-model="displayRetiredProducts"
                            color="success"
                            variant="3d"
                            @update:checked="handleRetiredChange"
                        />
                    </div>
                </DFormRow>
            </section>
            <section>
                <DRow
                    class="mb-3"
                    align-horizontal="start"
                    align-vertical="end"
                >
                    <DCol
                        sm="6"
                        class="text-left"
                    >
                        <h2 class="h4">
                            {{ __('customers.invoices.invoice_items_section') }}
                        </h2>
                    </DCol>
                    <DCol
                        sm="6"
                        class="text-right"
                    >
                        <DButton
                            id="table-configuration"
                            icon="cog-outline"
                            :label="__('messages.list_configuration')"
                            @click="showListViewModal = true"
                        />
                    </DCol>
                </DRow>
                <Form
                    :customer="customer"
                    :invoice="invoice"
                    :errors="errors"
                    :discount-types="discountTypes"
                    :products="productsList"
                    :user-list-view="userListView"
                    @preview="preview"
                />
            </section>
        </DCard>
        <DocumentPreSavePreview
            v-if="pdfSource !== null && numPages !== null"
            :title="__('customers.invoices.form.invoice_preview')"
            :pdf-source="pdfSource"
            :num-pages="numPages"
            :show-modal="showModal"
            @close="showModal = false"
            @confirm="submitData"
        />
        <DModal
            :title="__('customers.invoices.vat_id_is_mandatory')"
            is-error
            :show="modalHasErrors"
            @close="modalHasErrors = false"
        >
            <div class="fix-padding">
                <DRow class="fix-margin">
                    <DCol>
                        <p>
                            {{ __('customers.customers.set_customer_vat_number') }}
                            <a
                                :href="$route('customers.customers.show', { customer: customer.id })"
                                target="_blank"
                            >
                                {{ __('customers.customers.go_to_profile') }}
                            </a>
                        </p>
                    </DCol>
                </DRow>
            </div>
        </DModal>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="userListView"
            @close-modal="showListViewModal = false"
        />
        <DModal
            v-model:show="showVatWarningModal"
            :title="__('customers.invoices.vat_invalid_warning.title')"
            :message="__('customers.invoices.vat_invalid_warning.message', { vat: customer.billing_vat_number ?? '' })"
            is-confirm
            is-error
            @confirm="showVatWarningModal = false"
            @dismiss="$inertia.visit($route('customers.customers.invoices.index', { customer: customer.id }))"
        />
    </div>
</template>

<script>
import CustomerBase from '~@/Pages/Customers/Base'
import App from '~@/App.vue'

import Form from '~@/Components/Customers/Invoices/Form'

import NProgress from 'nprogress'
import DocumentPreSavePreview from '~@/Components/Customers/DocumentPreSavePreview'
import pdf from 'vue3-pdf'
import ListViewModal from '~@/Components/ListView/ListViewModal.vue'

export default {
    name: 'CustomerInvoiceCreate',
    components: {
        DocumentPreSavePreview,
        Form,
        ListViewModal
    },
    layout: [App, CustomerBase],
    props: {
        customer: {
            type: Object,
            default () {
                return {}
            }
        },
        invoice: {
            type: Object,
            default () {
                return {}
            }
        },
        taxData: {
            type: Object,
            default () {
                return {}
            }
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        hasSepaMandate: {
            type: Boolean,
            default: () => false
        },
        paymentMethods: {
            type: Array,
            default: () => { return [] }
        },
        paymentTerms: {
            type: Array,
            default: () => { return [] }
        },
        products: {
            type: Array,
            default: () => { return [] }
        },
        retired: {
            type: Boolean,
            default: false,
            required: false
        },
        userListView: {
            type: Array,
            default: () => {
                return []
            }
        },
        vatIdInvalid: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            showModal: false,
            pdfSource: null,
            numPages: null,
            errors: {},
            form: null,
            paymentMethod: null,
            paymentTerm: null,
            modalHasErrors: false,
            availablePaymentMethods: this.paymentMethods ?? null,
            displayRetiredProducts: false,
            productsList: this.products,
            showListViewModal: false,
            showVatWarningModal: this.vatIdInvalid
        }
    },
    computed: {
        tenantSettings () {
            return this.$page.props.tenantSettings
        }
    },
    methods: {
        submitData () {
            this.form.paymentMethod = this.paymentMethod
            this.form.paymentTerm = this.paymentTerm
            this.form.post(this.$route('customers.customers.invoices.store', {
                customer: this.customer.id
            }), {
                onSuccess: () => {
                    this.showModal = false
                    this.modalHasErrors = false
                },
                onError: (error) => {
                    if (Object.hasOwn(error, 'is_invalid_vat_id') && error.is_invalid_vat_id === true) {
                        this.showModal = false
                        this.modalHasErrors = true
                    }
                }
            })
        },
        preview (form) {
            this.form = form
            this.pdfSource = null
            this.numPages = null
            this.errors = {}

            window.axios.post(this.$route('customers.customers.invoices.preview', {
                customer: this.customer.id
            }), {
                invoiceItems: form.invoiceItems,
                paymentMethod: this.paymentMethod,
                paymentTerm: this.paymentTerm
            }, {
                responseType: 'blob'
            })
                .then(response => {
                    NProgress.done()

                    const blob = new Blob([response.data])

                    this.loading = false
                    this.pdfSource = URL.createObjectURL(blob)

                    pdf.createLoadingTask(this.pdfSource)
                        .promise.then(pdf => {
                            this.numPages = pdf.numPages
                        })

                    this.showModal = true
                })
                .catch((error) => {
                    this.loading = false

                    error.response.data.text().then(text => {
                        const response = JSON.parse(text)

                        this.errors = response.errors

                        // Trigger flash error message for general errors
                        if (response.errors && response.errors.general) {
                            this.$page.props.flash = {
                                errorMessages: response.errors.general.join(', ')
                            }
                        }
                    })
                })
        },
        handleRetiredChange (value) {
            this.displayRetiredProducts = value
            this.fetchProducts()
        },
        fetchProducts () {
            window.axios.get(this.$route('products.dropdown.products_with_product_group_list', {
                retired: this.displayRetiredProducts ? 1 : 0
            }))
                .then((res) => {
                    this.productsList = res.data
                })
        }
    }
}
</script>

<style scoped lang="scss">
.fix-padding {
    padding: 20px;
}
.fix-margin {
    margin-top: 16px;
}
</style>
